/**
 * orderParty — shared helper that resolves the "primary" identity
 * label and the "contact person" secondary line for an order.
 *
 * Feb 2026 — Wholesale + retail ops want the BUSINESS NAME to be the
 * headline everywhere an order surfaces (list card, detail view, PDF
 * bill-to, dispatch queue, driver dashboard, email/notification
 * subjects). The individual contact person still matters — they're
 * the human to call — but they belong on a secondary line so ops
 * scans the fleet of orders by business first.
 *
 * Data source: orders are enriched server-side with
 * `customer_business_name` (from `export_customers.business_name` or
 * `customer_profiles.business_name` — see
 * `core/order_enrichment.attach_business_names_to_orders`). When the
 * business name is not set (Walk-In, one-off orders), we gracefully
 * fall back to `customer_name` as the primary and hide the secondary
 * line so we never render "Walk-In · Walk-In".
 */

export const orderPartyPrimary = (o) => {
  const biz = String((o && o.customer_business_name) || '').trim();
  if (biz) return biz;
  const nm = String((o && o.customer_name) || '').trim();
  if (nm) return nm;
  return 'Direct';
};

export const orderPartyContact = (o) => {
  const biz = String((o && o.customer_business_name) || '').trim();
  const nm = String((o && o.customer_name) || '').trim();
  // Only surface the contact when the business is different from the
  // contact — avoids "ACME Trading · ACME Trading" duplicates.
  if (biz && nm && biz.toLowerCase() !== nm.toLowerCase()) return nm;
  return '';
};

// Convenience — single-line renderer for cases where we can't spare a
// second line (subject lines, toast titles, notification bodies).
// Falls back to just the primary when there's no distinct contact.
export const orderPartyOneLine = (o) => {
  const primary = orderPartyPrimary(o);
  const contact = orderPartyContact(o);
  return contact ? `${primary} (${contact})` : primary;
};
